import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import { MenuItemImage } from "../MenuItemImage";

export const CartNavButton = () => {
  const { count, setCartOpen } = useStore();
  return (
    <button
      data-testid="nav-cart-button"
      onClick={() => setCartOpen(true)}
      className="relative rounded-full p-2 text-brand-cream transition-colors hover:text-brand-gold"
      aria-label="View cart"
    >
      <ShoppingBag className="h-5 w-5" />
      {count > 0 && (
        <span
          data-testid="nav-cart-count"
          className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-gold text-[10px] font-bold text-brand-brown"
        >
          {count}
        </span>
      )}
    </button>
  );
};

const CartRow = ({ line }) => {
  const { setQty, remove } = useStore();
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white p-3 ring-1 ring-brand-brown/5" data-testid={`cart-row-${line.id}`}>
      <MenuItemImage src={line.image_url} alt={line.name} className="h-14 w-14 rounded-xl" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-serif text-sm font-semibold text-brand-ink">{line.name}</p>
        <p className="text-xs text-brand-mute">₹{line.price} each</p>
        <div className="mt-1.5 flex items-center gap-2">
          <button data-testid={`cart-minus-${line.id}`} onClick={() => setQty(line.id, line.qty - 1)} className="rounded-full bg-brand-beige p-1 text-brand-brown" aria-label="Decrease quantity">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-5 text-center text-sm font-bold text-brand-ink" data-testid={`cart-qty-${line.id}`}>{line.qty}</span>
          <button data-testid={`cart-plus-${line.id}`} onClick={() => setQty(line.id, line.qty + 1)} className="rounded-full bg-brand-beige p-1 text-brand-brown" aria-label="Increase quantity">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <span className="font-serif text-sm font-bold text-brand-red">₹{line.price * line.qty}</span>
        <button data-testid={`cart-remove-${line.id}`} onClick={() => remove(line.id)} className="text-brand-mute transition-colors hover:text-brand-red" aria-label={`Remove ${line.name}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export const CartWidget = () => {
  const { cart, cartRestaurant, cartOpen, setCartOpen, subtotal, count, cartNotice, dismissCartNotice } = useStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <>
      {cartNotice && (
        <div className="fixed inset-x-4 top-20 z-[75] mx-auto flex max-w-2xl items-start gap-3 rounded-2xl bg-white p-4 text-sm text-brand-brown shadow-xl ring-1 ring-brand-gold/40" role="status" data-testid="legacy-cart-notice">
          <p className="flex-1">{cartNotice}</p>
          <button onClick={dismissCartNotice} className="font-bold text-brand-red" aria-label="Dismiss cart message"><X className="h-4 w-4" /></button>
        </div>
      )}
      <AnimatePresence>
        {count > 0 && !cartOpen && pathname !== "/checkout" && (
          <motion.button
            data-testid="cart-mobile-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            onClick={() => setCartOpen(true)}
            className="fixed inset-x-4 bottom-4 z-40 flex items-center justify-between rounded-full bg-brand-brown px-6 py-4 text-brand-cream shadow-2xl md:hidden"
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <ShoppingBag className="h-4 w-4 text-brand-gold" /> {count} item{count > 1 ? "s" : ""}
            </span>
            <span className="font-serif text-lg font-bold text-brand-gold">₹{subtotal} · View Cart</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setCartOpen(false)}
              className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
            />
            <motion.aside
              data-testid="cart-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col bg-brand-cream shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-brand-brown/10 px-6 py-5">
                <h2 className="font-serif text-2xl font-bold text-brand-ink">Your Order</h2>
                <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} className="rounded-full p-2 text-brand-brown hover:bg-brand-beige" aria-label="Close cart">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5">
                {cart.length > 0 && cartRestaurant.display_name && (
                  <p className="rounded-xl bg-brand-gold/10 px-4 py-3 text-sm font-semibold text-brand-brown" data-testid="cart-restaurant-name">
                    Ordering from {cartRestaurant.display_name}
                  </p>
                )}
                {cart.length === 0 ? (
                  <p className="py-16 text-center text-sm text-brand-mute" data-testid="cart-empty-message">
                    Your cart is empty. Add something delicious from the menu!
                  </p>
                ) : (
                  cart.map((line) => <CartRow key={line.id} line={line} />)
                )}
              </div>

              {cart.length > 0 && (
                <div className="border-t border-brand-brown/10 bg-white px-6 py-5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-mute">Subtotal</span>
                    <span className="font-serif text-2xl font-bold text-brand-ink" data-testid="cart-subtotal">₹{subtotal}</span>
                  </div>
                  <p className="mt-1 text-xs text-brand-mute">Delivery charges calculated at checkout.</p>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={goCheckout}
                    className="mt-4 w-full rounded-full bg-brand-gold py-4 text-sm font-bold uppercase tracking-[0.14em] text-brand-brown shadow-lg shadow-brand-gold/25 transition-transform duration-300 hover:scale-[1.02]"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
